import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Spinner, Tooltip } from '@epam/promo';
import { ReactComponent as CloseIcon } from '@epam/assets/icons/common/navigation-close-24.svg';
import { ReactComponent as InfoIcon } from '@epam/assets/icons/common/notification-info-outline-18.svg';

import { useGenerateAISuggestion } from 'hooks';
import { useAISuggestionStore } from 'store/ai-suggestion';
import { AISuggestionSwitcher } from './AISuggestionSwitcher';
import { AISuggestionBlockProps } from './AISuggestionBlock.model';
import css from './AISuggestionBlock.module.scss';

export const AISuggestionBlock = ({
  options,
  text,
  onClose,
  onApply,
}: AISuggestionBlockProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const queryClient = useQueryClient();

  const { isLoading, data, refetch } = useGenerateAISuggestion({
    ...options,
    text,
  });
  const setActiveAISuggestion = useAISuggestionStore(
    (store) => store.setActiveAISuggestion,
  );

  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: ['generateDescription', options.tab],
      });
    },
    [queryClient, options.tab],
  );

  useEffect(() => {
    if (data) {
      setSuggestions((prevState) => [...prevState, data]);
    }
  }, [data]);
  useEffect(() => setActive(suggestions.length - 1), [suggestions]);

  const regenerate = () => {
    setActiveAISuggestion(null);
    refetch();
  };

  return (
    <div className={css['suggestion-block__wrapper']}>
      <div className={css['suggestion-block__header-container']}>
        <p>
          AI Suggestion
          <Tooltip
            content="Warning. Please note that this text was generated using AI and may include irrelevant data.
            Before using it, please read it carefully. If necessary, you can modify the original request and 
            regenerate the text or make changes to it after transferring it."
            placement="top">
            <InfoIcon />
          </Tooltip>
        </p>
        <CloseIcon onClick={() => onClose(isLoading, suggestions.length)} />
      </div>
      <div
        className={`
        ${css['suggestion-block__body-container']} ${
          isLoading ? css['suggestion-block__body-spinner'] : ''
        }
      `}>
        {isLoading && <Spinner />}
        {!isLoading && (
          <div className={css['suggestion-block__body-description']}>
            {suggestions[active]}
          </div>
        )}
      </div>
      {!isLoading && (
        <div className={css['suggestion-block__footer-container']}>
          <AISuggestionSwitcher
            active={active}
            amount={suggestions?.length || 0}
            setActive={setActive}
          />
          <div className={css['suggestion-block__action-container']}>
            <Button
              caption="Regenerate"
              fill="white"
              size="30"
              onClick={regenerate}
            />
            <Button
              caption="Apply"
              size="30"
              onClick={() => onApply(suggestions[active])}
            />
          </div>
        </div>
      )}
    </div>
  );
};
